import { Container, IDestroyOptions, Point, Sprite } from "pixi.js";
import { MapZone } from "./MapZone";
import { MapZonesViewDescription } from "../types/MapZonesViewDescription";
import { MapViewConfig } from "../configs/MapViewConfig";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { ZoomCamera } from "../../../../common/ZoomCamera";
import { Event } from "event-system";
import { IMarketplaceMapModel } from "../models/map/IMarketplaceMapModel";
import { IMarketplaceController } from "../models/controller/IMarketplaceController";
import { IMarketplaceViewModel } from "../models/IMarketplaceViewModel";
import { IPlayerModel } from "../../../../common/models/IPlayerModel";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../../../analytics/events/VirtualScreenEvent";
import { MarketplaceResourcePackage } from "../../resources/MarketplaceResourcePackage";

export class MarketplaceMap extends Container {
    private static readonly MAP_DEFAULT_POSITION = new Point(0, 201);
    private static readonly MAP_ZOOM_POWER = 2.3;

    public readonly onMapZoomChange: Event<boolean>;
    private readonly mapZonesViewDescription: Array<MapZonesViewDescription>;
    private readonly marketplaceResources: MarketplaceResourcePackage;
    private readonly canvasScaler: ICanvasScaler;
    private readonly marketplaceController: IMarketplaceController;
    private readonly marketplaceMapModel: IMarketplaceMapModel;
    private readonly marketplaceMapViewModel: IMarketplaceViewModel;
    private readonly playerModel: IPlayerModel;
    private readonly analyticsProvider: IAnalyticProviderWrapper;

    private zoomCamera: ZoomCamera;
    private mapBg: Sprite;
    private mapZonesBg: Array<Sprite>;
    private mapZones: Array<MapZone>;
    private mapZonesContainer: Container;
    private pinsContainer: Container;
    private mapZoneBgScale: number;

    public constructor(
        marketplaceResources: MarketplaceResourcePackage,
        canvasScaler: ICanvasScaler,
        marketplaceController: IMarketplaceController,
        marketplaceMapModel: IMarketplaceMapModel,
        marketplaceMapViewModel: IMarketplaceViewModel,
        playerModel: IPlayerModel,
        analyticsProvider: IAnalyticProviderWrapper
    ) {
        super();
        this.marketplaceResources = marketplaceResources;
        this.canvasScaler = canvasScaler;
        this.marketplaceController = marketplaceController;
        this.marketplaceMapModel = marketplaceMapModel;
        this.marketplaceMapViewModel = marketplaceMapViewModel;
        this.playerModel = playerModel;
        this.analyticsProvider = analyticsProvider;
        this.onMapZoomChange = new Event<boolean>();
        this.mapZonesViewDescription = MapViewConfig.createMapZonesViewDescription(this.marketplaceResources);
        this.mapZones = [];
        this.mapZonesBg = [];
        this.mapZoneBgScale = 1 / MarketplaceMap.MAP_ZOOM_POWER;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.addedToStage.bind(this));
    }

    private build(): void {
        this.mapBg = new Sprite(this.marketplaceResources.assets_views_marketplace_map_f_all_png);
        this.addChild(this.mapBg);
        this.createMapZonesBackground();

        this.mapZonesContainer = new Container();
        this.addChild(this.mapZonesContainer);

        this.pinsContainer = new Container();
        this.addChild(this.pinsContainer);

        const mapBaseLine = new Sprite(this.marketplaceResources.assets_views_marketplace_map_base_line_png);
        mapBaseLine.position.set(0, -2);
        this.mapZonesContainer.addChild(mapBaseLine);

        this.createMapZones();
    }

    private createMapZonesBackground(): void {
        this.mapZonesViewDescription.forEach((item) => {
            const mapZoneBg = new Sprite(item.mapZoneDescription.mapZoneBg);
            mapZoneBg.scale.set(this.mapZoneBgScale);
            mapZoneBg.position = item.mapZoneDescription.mapZoneBgPosition;
            this.mapZonesBg.push(mapZoneBg);
            this.addChild(mapZoneBg);
        });
    }

    private createMapZones(): void {
        const allAreas = this.marketplaceMapModel.areas.value;
        this.mapZonesViewDescription.forEach((mapZoneViewItem, index) => {
            const mapZoneInfo = allAreas.find(areaInfoItem => areaInfoItem.area === mapZoneViewItem.area);
            const mapZone = new MapZone(
                this.marketplaceResources,
                this.marketplaceController,
                this.marketplaceMapModel,
                this.marketplaceMapViewModel,
                this.playerModel,
                mapZoneViewItem,
                this.pinsContainer,
                this.mapZonesBg[index],
                mapZoneInfo,
                this.analyticsProvider
            );
            mapZone.onMapZonePinClick.add(this.mapZoneClickHandle.bind(this, mapZone));
            mapZone.position.set(mapZoneViewItem.position.x, mapZoneViewItem.position.y);
            this.mapZones.push(mapZone);
            this.mapZonesContainer.addChild(mapZone);
        });
    }

    private addedToStage(): void {
        this.zoomCamera = new ZoomCamera(this, MarketplaceMap.MAP_ZOOM_POWER);
        this.mapZonesBg.forEach((item) => item.alpha = 0);
    }

    public async mapZoomOut(): Promise<void> {
        this.mapZones.forEach(item => item.focusOut());
        this.onMapZoomChange.emit(false);
        await this.zoomCamera.zoomOut();
        this.interactiveMapZones(true);
    }

    private async mapZoneClickHandle(mapZone: MapZone): Promise<void> {
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Click on area yellow pin in the main areas map", this.analyticsProvider.currentEventIndex))
        this.mapZones.forEach(async item => {
            const isSelected = mapZone === item;
            item.focusIn(isSelected);
            if (isSelected) {
                this.mapZonesContainer.setChildIndex(item, this.mapZonesContainer.children.length - 1);
                const zoomTargetPosition = new Point(item.x + item.zoomTargetPosition.x, item.y + 185);
                await this.zoomCamera.zoomIn(zoomTargetPosition);
                this.onMapZoomChange.emit(true);
            }
        });
        this.interactiveMapZones(false);
    }

    private interactiveMapZones(isLock: boolean): void {
        this.mapZones.forEach(mapZone => mapZone.eventMode = isLock ? "none" : "auto");
        this.pinsContainer.eventMode = isLock ? "static" : "none";
    }

    private layout(width: number, height: number): void {
        this.width = width;
        this.position.y = MarketplaceMap.MAP_DEFAULT_POSITION.y;
        this.mapZonesContainer.position.set(3, 246);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.mapBg.removeAllListeners("pointerup");
        this.zoomCamera.killAnimations();
        this.mapZones.forEach(mapZone => mapZone.onMapZonePinClick.removeAll());
        super.destroy(options);
    }
}
